/**
 * Mobile Navigation - Bottom navigation bar for mobile/tablet screens
 * 5 items: Home, Markets, Search, Portfolio, More
 */
import { useState, useEffect, useRef } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  TrendingUp,
  Search,
  Briefcase,
  Menu,
  X,
  Eye,
  Filter,
  Brain,
  Bell,
  Settings,
  Layers,
  BarChart3,
  Calendar
} from 'lucide-react'
import { useIsDesktop } from '../../hooks/useBreakpoint'
import './MobileNav.css'

interface NavItem {
  path: string
  icon: React.ReactNode
  label: string
}

interface MobileNavProps {
  onSearchClick?: () => void
}

// Items in the More bottom sheet
const MORE_ITEMS: NavItem[] = [
  { path: '/watchlist', icon: <Eye size={20} />, label: 'Watchlist' },
  { path: '/screener', icon: <Filter size={20} />, label: 'Screener' },
  { path: '/strategies', icon: <BarChart3 size={20} />, label: 'Strategy' },
  { path: '/intelligence', icon: <Brain size={20} />, label: 'Intelligence' },
  { path: '/alerts', icon: <Bell size={20} />, label: 'Alerts' },
  { path: '/options', icon: <Layers size={20} />, label: 'Options' },
  { path: '/earnings', icon: <Calendar size={20} />, label: 'Earnings' },
  { path: '/settings', icon: <Settings size={20} />, label: 'Settings' },
]

export function MobileNav({ onSearchClick }: MobileNavProps) {
  const [showMore, setShowMore] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()

  // Close sheet when route changes
  useEffect(() => {
    setShowMore(false)
  }, [location.pathname])

  // Focus search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [showSearch])

  // Handle swipe down to close sheet
  useEffect(() => {
    const sheet = sheetRef.current
    if (!sheet || !showMore) return

    let startY = 0
    let currentY = 0

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY
    }

    const handleTouchMove = (e: TouchEvent) => {
      currentY = e.touches[0].clientY
      const diff = currentY - startY
      if (diff > 0) {
        sheet.style.transform = `translateY(${diff}px)`
      }
    }

    const handleTouchEnd = () => {
      const diff = currentY - startY
      if (diff > 100) {
        setShowMore(false)
      }
      sheet.style.transform = ''
    }

    sheet.addEventListener('touchstart', handleTouchStart, { passive: true })
    sheet.addEventListener('touchmove', handleTouchMove, { passive: true })
    sheet.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart)
      sheet.removeEventListener('touchmove', handleTouchMove)
      sheet.removeEventListener('touchend', handleTouchEnd)
    }
  }, [showMore])

  // Don't render on desktop - must come AFTER all hooks (Rules of Hooks)
  if (isDesktop) return null

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick()
    } else {
      setShowSearch(true)
    }
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/stock/${searchQuery.toUpperCase()}`)
      setSearchQuery('')
      setShowSearch(false)
    }
  }

  const handleSearchClose = () => {
    setShowSearch(false)
    setSearchQuery('')
  }

  // Check if current path is in More items
  const isMoreActive = MORE_ITEMS.some(item => location.pathname.startsWith(item.path))

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="mobile-nav" role="navigation" aria-label="Main navigation">
        {/* Home */}
        <NavLink
          to="/"
          end
          className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon"><LayoutDashboard size={22} /></span>
          <span className="nav-label">Home</span>
        </NavLink>

        {/* Markets */}
        <NavLink
          to="/markets"
          className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon"><TrendingUp size={22} /></span>
          <span className="nav-label">Markets</span>
        </NavLink>

        {/* Search */}
        <button
          className="mobile-nav-item"
          onClick={handleSearchClick}
          aria-label="Search"
        >
          <span className="nav-icon"><Search size={22} /></span>
          <span className="nav-label">Search</span>
        </button>

        {/* Portfolio */}
        <NavLink
          to="/portfolio"
          className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon"><Briefcase size={22} /></span>
          <span className="nav-label">Portfolio</span>
        </NavLink>

        {/* More */}
        <button
          className={`mobile-nav-item ${showMore || isMoreActive ? 'active' : ''}`}
          onClick={() => setShowMore(true)}
          aria-label="More pages"
          aria-expanded={showMore}
        >
          <span className="nav-icon"><Menu size={22} /></span>
          <span className="nav-label">More</span>
        </button>
      </nav>

      {/* More Bottom Sheet */}
      {showMore && (
        <>
          <div className="mobile-sheet-backdrop" onClick={() => setShowMore(false)} />
          <div className="mobile-sheet" ref={sheetRef} role="dialog" aria-label="More pages">
            <div className="mobile-sheet-handle" aria-hidden="true" />
            <div className="mobile-sheet-content">
              {MORE_ITEMS.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `mobile-sheet-item ${isActive ? 'active' : ''}`}
                  onClick={() => setShowMore(false)}
                >
                  <span className="sheet-item-icon">{item.icon}</span>
                  <span className="sheet-item-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Full-screen Search Overlay */}
      {showSearch && (
        <div className="mobile-search-overlay">
          <div className="mobile-search-header">
            <form onSubmit={handleSearchSubmit} className="mobile-search-form">
              <Search size={20} className="mobile-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                placeholder="Search tickers, companies..."
                className="mobile-search-input"
                autoComplete="off"
                autoCapitalize="characters"
              />
            </form>
            <button
              className="mobile-search-close"
              onClick={handleSearchClose}
              aria-label="Close search"
            >
              <X size={24} />
            </button>
          </div>
          <div className="mobile-search-results">
            {searchQuery && (
              <button
                className="mobile-search-result"
                onClick={() => {
                  navigate(`/stock/${searchQuery}`)
                  handleSearchClose()
                }}
              >
                <TrendingUp size={20} />
                <span>Go to {searchQuery}</span>
              </button>
            )}
            <p className="mobile-search-hint">Enter a ticker symbol to view stock details</p>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileNav
